/**
 * MessageBubble component - renders a single message in the conversation
 * Supports text, attachments, reactions, replies, read receipts
 */
'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCheck, FileText, Download, Eye, Play, Reply, Pin,
  ThumbsUp, Heart, Laugh, Frown, CheckSquare, ExternalLink, Mail,
  MoreVertical, MapPin, Mic, Camera, Trash2
} from 'lucide-react';
import type { ThreadMessage, Attachment } from './types';
import { QUICK_REACTIONS } from './types';
import { emit } from '@/lib/typedEventBus';
import { formatTimeSAST } from '@/lib/sastTime';

type ReviewAction = 'acknowledge' | 'ai_hold' | 'resume_review' | 'resolve';

interface MessageBubbleProps {
  message: ThreadMessage;
  isOwn: boolean;
  isDark?: boolean;
  currentUserId?: number;
  threadMemberCount?: number;
  showAvatar?: boolean;
  senderRole?: string; // Role from module_access (owner, admin, supervisor, member)

  // Search highlight
  searchQuery?: string;
  isSearchMatch?: boolean;

  // Actions
  onReply?: (message: ThreadMessage) => void;
  onReaction?: (messageId: string, emoji: string) => void;
  onEdit?: (messageId: string, newContent: string) => void;
  onDelete?: (messageId: string) => void;
  onImageClick?: (url: string) => void;
  onAddToTasks?: (message: ThreadMessage) => void;
  onPinMention?: (message: ThreadMessage) => void;
  onAvatarClick?: (message: ThreadMessage) => void; // New: click sender avatar
  onReviewAction?: (message: ThreadMessage, action: ReviewAction) => Promise<{ review_status?: string; appointment_status?: string } | void> | { review_status?: string; appointment_status?: string } | void;
  onOpenProposalEmail?: (proposalId: string) => void;
  proposalEmailLoadingId?: string | null;

  // Managed task state
  isInTasks?: boolean;

  // Adding to task state
  isAddingToTask?: boolean;
}

// Helper to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Helper to format duration for voice notes
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getLocationMapHref(latitude: number, longitude: number): string {
  return `https://www.google.com/maps?q=${latitude},${longitude}`;
}

function getLocationPreviewUrl(latitude: number, longitude: number): string {
  const center = `${latitude},${longitude}`;
  return `https://staticmap.openstreetmap.de/staticmap.php?center=${encodeURIComponent(center)}&zoom=15&size=600x320&maptype=mapnik&markers=${encodeURIComponent(`${center},red-pushpin`)}`;
}

// Helper to highlight search matches
function highlightSearchMatch(content: string, query: string): React.ReactNode {
  if (!query || !content) return content;

  const lowerQuery = query.toLowerCase();
  const lowerContent = content.toLowerCase();
  const startIndex = lowerContent.indexOf(lowerQuery);

  if (startIndex === -1) return content;

  const before = content.substring(0, startIndex);
  const match = content.substring(startIndex, startIndex + query.length);
  const after = content.substring(startIndex + query.length);

  return (
    <>
      {before}
      <mark className="bg-yellow-300 text-gray-900 px-0.5 rounded">{match}</mark>
      {after}
    </>
  );
}

// Helper to render message content with clickable links
function renderContentWithLinks(content: string, isDark: boolean, isOwn: boolean): React.ReactNode {
  // Guard against non-string content (null, undefined, objects, etc.)
  if (!content || typeof content !== 'string') {
    return typeof content === 'object' ? JSON.stringify(content) : String(content || '');
  }

  // URL regex pattern that matches http, https URLs
  const urlPattern = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;

  const parts = content.split(urlPattern);

  if (parts.length === 1) {
    return content; // No URLs found
  }

  const splitTrailingUrlPunctuation = (url: string): { cleanUrl: string; trailing: string } => {
    const match = url.match(/([.,!?;:…]+)$/u);

    if (!match) {
      return { cleanUrl: url, trailing: '' };
    }

    return {
      cleanUrl: url.slice(0, -match[1].length),
      trailing: match[1],
    };
  };

  return parts.map((part, index) => {
    if (part.match(urlPattern)) {
      const { cleanUrl, trailing } = splitTrailingUrlPunctuation(part);

      return (
        <React.Fragment key={index}>
          <a
            href={cleanUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline hover:opacity-80 transition-opacity ${isDark ? 'text-blue-300' : 'text-blue-600'
              } ${isOwn ? '!text-white/90 !no-underline hover:!underline' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            {cleanUrl}
          </a>
          {trailing}
        </React.Fragment>
      );
    }
    return part;
  });
}

function openWorkItemWithAiReview(itemId?: string | null, permalinkUrl?: string | null) {
  if (typeof window === 'undefined') return;

  if (permalinkUrl) {
    try {
      const targetUrl = new URL(permalinkUrl, window.location.origin);
      targetUrl.searchParams.set('tab', 'ai_review');

      const openedWindow = window.open(targetUrl.toString(), '_blank', 'noopener,noreferrer');
      if (!openedWindow) {
        window.location.assign(targetUrl.toString());
      }
      return;
    } catch (error) {
      console.warn('[MessageBubble] Failed to open work item permalink, falling back to in-app opener:', error);
    }
  }

  if (!itemId) return;

  emit('openWorkItem', {
    id: itemId,
    options: {
      initialTab: 'ai_review',
    },
  });
}

function getWorkItemAiReviewHref(permalinkUrl?: string | null): string | null {
  if (!permalinkUrl || typeof window === 'undefined') {
    return null;
  }

  try {
    const targetUrl = new URL(permalinkUrl, window.location.origin);
    targetUrl.searchParams.set('tab', 'ai_review');
    return targetUrl.toString();
  } catch (error) {
    console.warn('[MessageBubble] Failed to build work item AI review permalink:', error);
    return null;
  }
}

function openPermalinkDirect(permalinkUrl?: string | null) {
  if (typeof window === 'undefined' || !permalinkUrl) return;

  try {
    const targetUrl = new URL(permalinkUrl, window.location.origin);
    const openedWindow = window.open(targetUrl.toString(), '_blank', 'noopener,noreferrer');
    if (!openedWindow) {
      window.location.assign(targetUrl.toString());
    }
  } catch (error) {
    console.warn('[MessageBubble] Failed to open permalink directly:', error);
  }
}

function getDirectPermalinkHref(permalinkUrl?: string | null): string | null {
  if (!permalinkUrl || typeof window === 'undefined') {
    return null;
  }

  try {
    return new URL(permalinkUrl, window.location.origin).toString();
  } catch (error) {
    console.warn('[MessageBubble] Failed to build direct permalink href:', error);
    return null;
  }
}

function parsePositiveInt(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return null;
  }
  return Math.floor(n);
}

// Role badge config
const ROLE_BADGES: Record<string, { emoji: string; color: string; label: string }> = {
  owner: { emoji: '👑', color: 'bg-amber-500', label: 'Owner' },
  admin: { emoji: '⭐', color: 'bg-purple-500', label: 'Admin' },
  supervisor: { emoji: '⭐', color: 'bg-blue-500', label: 'Supervisor' }
};

/** Long AI Operations review posts: collapse body by default; summary card + actions stay visible. */
const AI_OPS_REVIEW_BODY_COLLAPSE_MIN_CHARS = 320;

export default function MessageBubble({
  message,
  isOwn,
  isDark = false,
  currentUserId,
  threadMemberCount = 2,
  showAvatar = true,
  senderRole,
  searchQuery,
  isSearchMatch,
  onReply,
  onReaction,
  onEdit,
  onDelete,
  onImageClick,
  onAddToTasks,
  onPinMention,
  onAvatarClick,
  onReviewAction,
  onOpenProposalEmail,
  proposalEmailLoadingId,
  isInTasks = false,
  isAddingToTask
}: MessageBubbleProps) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPos, setActionMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [reviewStatusOverride, setReviewStatusOverride] = useState<string | null>(null);
  const [reviewActionLoading, setReviewActionLoading] = useState<ReviewAction | null>(null);
  const [aiOpsDetailsExpanded, setAiOpsDetailsExpanded] = useState(false);
  const [locationPreviewFailed, setLocationPreviewFailed] = useState(false);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const messageId = String(
    message._id
    || message.id
    || (message as ThreadMessage & { message_id?: string }).message_id
    || message.temp_id
    || ''
  );

  // Calculate read receipt status
  const readByOthers = (message.read_by || []).filter(r => String(r.user_id) !== String(currentUserId));
  const allRead = readByOthers.length >= (threadMemberCount - 1);
  const someRead = readByOthers.length > 0;

  // Check if message is a location share
  const isLocationMessage = message.message_type === 'location' && message.location;
  const locationMapHref = isLocationMessage && message.location
    ? getLocationMapHref(message.location.latitude, message.location.longitude)
    : null;
  const locationPreviewUrl = isLocationMessage && message.location
    ? getLocationPreviewUrl(message.location.latitude, message.location.longitude)
    : null;

  // Check if message is a voice note
  const isVoiceNote = message.message_type === 'voice_note'
    || message.content_type === 'voice'
    || (
      message.attachments?.length === 1
      && message.attachments.some((att) => (att.contentType || att.mimeType || '').startsWith('audio/'))
      && (!message.content || message.content === '🎤 Voice note' || message.content === '🎤 Voice message')
    );

  // Check if message is pending (offline queue)
  const isPending = message.pending || !!message.offline_id;
  const isFailed = message.failed;
  const isAiOperationsReviewAlert = message.metadata?.event_type === 'ai_operations_review_flagged' && Boolean(message.metadata?.review_item_id);
  const isAiOperationsThreadReply = message.metadata?.event_type === 'ai_operations_thread_reply';
  const isAiProposalThreadReply = message.metadata?.event_type === 'ai_proposal_manager_reply';
  const isAiProposalWorkflowUpdate = message.metadata?.event_type === 'ai_proposal_workflow_update';
  const aiVerdictRaw = typeof message.metadata?.ai_verdict === 'string'
    ? String(message.metadata.ai_verdict).trim().toLowerCase()
    : null;
  const confidenceFromMeta = parsePositiveInt(message.metadata?.confidence_score);
  const fieldPhotoCountMeta = parsePositiveInt(message.metadata?.field_photo_count);
  const fieldPhotoFlaggedMeta = parsePositiveInt(message.metadata?.field_photo_flagged_count);
  const showAiOpsFieldPhotoLine = Boolean(
    isAiOperationsReviewAlert && fieldPhotoCountMeta != null && fieldPhotoCountMeta > 0,
  );
  const showAiOpsReviewSummaryCard = Boolean(
    isAiOperationsReviewAlert && (
      Boolean(aiVerdictRaw)
      || (confidenceFromMeta != null && confidenceFromMeta <= 100)
      || showAiOpsFieldPhotoLine
    ),
  );
  const reviewStatus = reviewStatusOverride || message.metadata?.review_status || null;
  const canAcknowledgeReview = isAiOperationsReviewAlert && reviewStatus !== 'acknowledged' && reviewStatus !== 'on_hold' && reviewStatus !== 'resolved';
  const canAiHoldReview = isAiOperationsReviewAlert && reviewStatus !== 'on_hold' && reviewStatus !== 'resolved';
  const canResumeReview = isAiOperationsReviewAlert && reviewStatus === 'on_hold';
  const canResolveReview = isAiOperationsReviewAlert && reviewStatus !== 'resolved';
  const reviewStatusLabel = reviewStatus === 'resolved'
    ? 'Accepted'
    : reviewStatus === 'on_hold'
      ? 'AI on hold'
      : reviewStatus === 'acknowledged'
        ? 'Acknowledged'
        : 'Awaiting action';
  const appointmentAiReviewHref = getWorkItemAiReviewHref(message.metadata?.appointment_permalink_url);
  const jobAiReviewHref = getWorkItemAiReviewHref(
    message.metadata?.parent_job_permalink_url || message.metadata?.job_permalink_url
  );
  const reviewRecordAiReviewHref = getWorkItemAiReviewHref(message.metadata?.review_item_permalink_url);
  const proposalPermalinkHref = getDirectPermalinkHref(message.metadata?.proposal_url);
  const proposalPdfUrl = typeof message.metadata?.proposal_pdf_url === 'string'
    ? String(message.metadata.proposal_pdf_url).trim()
    : '';
  const proposalId = typeof message.metadata?.proposal_id === 'string'
    ? String(message.metadata.proposal_id).trim()
    : '';
  const appointmentReferenceLabel = isAiOperationsThreadReply ? 'Open AI review' : 'Open appointment';
  const jobReferenceLabel = isAiOperationsThreadReply ? 'Open job review' : 'Open job';
  const proposalReferenceLabel = 'Open actual proposal';
  const canShowAppointmentReference = Boolean(message.metadata?.appointment_id || message.metadata?.appointment_permalink_url);
  const canShowJobReference = Boolean(
    message.metadata?.parent_job_id || message.metadata?.job_id || message.metadata?.parent_job_permalink_url || message.metadata?.job_permalink_url
  );
  const canShowReviewRecordReference = Boolean(
    message.metadata?.review_item_id || message.metadata?.review_item_permalink_url
  );
  const canShowProposalReference = Boolean(message.metadata?.proposal_url);
  const canShowProposalPdf = Boolean(proposalPdfUrl);
  const canEmailProposal = Boolean(proposalId && onOpenProposalEmail && (isAiProposalThreadReply || isAiProposalWorkflowUpdate));
  const isProposalEmailLoading = Boolean(proposalId && proposalEmailLoadingId === proposalId);
  const showAiReferenceFooter = isAiOperationsReviewAlert || (
    (isAiOperationsThreadReply && (canShowAppointmentReference || canShowJobReference || canShowReviewRecordReference))
    || ((isAiProposalThreadReply || isAiProposalWorkflowUpdate) && (canShowProposalReference || canShowProposalPdf || canEmailProposal))
  );
  const referencePillClass = `inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80 ${isOwn ? 'bg-white/15 text-white' : isDark ? 'bg-gray-600 text-gray-100' : 'bg-white text-gray-700 border border-gray-300 shadow-sm'
    }`;

  const isEncryptedContent = typeof message.content === 'object' && Boolean((message.content as { encrypted?: boolean })?.encrypted);
  const plainTextForAiOpsCollapse =
    !isEncryptedContent && message.content && message.content !== '📎 Attachment' && message.content !== '🎤 Voice note'
      ? String(message.content)
      : '';
  const shouldCollapseAiOpsReviewBody = isAiOperationsReviewAlert
    && plainTextForAiOpsCollapse.length >= AI_OPS_REVIEW_BODY_COLLAPSE_MIN_CHARS;
  const aiOpsBodyExpandedEffective = aiOpsDetailsExpanded
    || (Boolean(searchQuery?.trim()) && Boolean(isSearchMatch));

  useEffect(() => {
    setReviewStatusOverride(message.metadata?.review_status || null);
  }, [message.metadata?.review_status]);

  useEffect(() => {
    setAiOpsDetailsExpanded(false);
  }, [messageId]);

  // Close the action menu when the message list scrolls — it's portaled to
  // document.body with fixed coordinates, so it would otherwise stay put while
  // its anchor message moves
  useEffect(() => {
    if (!showActionMenu) return;
    const close = () => setShowActionMenu(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [showActionMenu]);

  useEffect(() => {
    setLocationPreviewFailed(false);
  }, [locationPreviewUrl, messageId]);

  const handleReviewActionClick = async (action: ReviewAction) => {
    if (!onReviewAction || reviewActionLoading) return;

    try {
      setReviewActionLoading(action);
      const result = await onReviewAction(message, action);
      if (result?.review_status) {
        setReviewStatusOverride(result.review_status);
      }
    } finally {
      setReviewActionLoading(null);
    }
  };

  return (
    <div
      id={`msg-${messageId}`}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group px-2 sm:px-4 mb-2 ${
        // Raise the whole row above following messages while a menu is open —
        // otherwise the next bubble's stacking context paints over the dropdown
        showActionMenu || showReactionPicker ? 'relative z-40' : ''
      }`}
    >
      {/* Avatar for other users' messages in non-direct threads */}
      {!isOwn && showAvatar && threadMemberCount > 1 && (
        <button
          onClick={() => onAvatarClick?.(message)}
          className="flex-shrink-0 mr-2 cursor-pointer group/avatar relative"
          title={message.sender_name}
        >
          {message.sender_avatar ? (
            <img
              src={message.sender_avatar}
              alt={message.sender_name}
              className="w-8 h-8 rounded-full object-cover transition-transform group-hover/avatar:scale-110"
            />
          ) : (
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white transition-transform group-hover/avatar:scale-110 ${isDark ? 'bg-gray-600' : 'bg-gray-400'
              }`}>
              {message.sender_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
          {/* Role badge on avatar */}
          {senderRole && ROLE_BADGES[senderRole.toLowerCase()] && (
            <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] ${ROLE_BADGES[senderRole.toLowerCase()].color}`}
              title={ROLE_BADGES[senderRole.toLowerCase()].label}>
              {ROLE_BADGES[senderRole.toLowerCase()].emoji}
            </div>
          )}
        </button>
      )}
      {/* Spacer for messages without avatar to align properly */}
      {!isOwn && !showAvatar && threadMemberCount > 1 && (
        <div className="w-8 mr-2 flex-shrink-0" />
      )}
      <div
        className={`relative max-w-[85%] sm:max-w-[75%] ${isSearchMatch ? 'ring-2 ring-yellow-400' : ''
          }`}
      >
        {/* Reply preview */}
        {message.reply_to && (
          <div className={`text-xs mb-1 p-2 rounded-t-lg border-l-2 ${isOwn
            ? isDark ? 'bg-blue-800/30 border-blue-400 text-blue-300' : 'bg-blue-100 border-blue-500 text-blue-700'
            : isDark ? 'bg-gray-700/50 border-gray-500 text-gray-400' : 'bg-gray-100 border-gray-400 text-gray-600'
            }`}>
            <p className="font-medium">{message.reply_to.sender_name}</p>
            <p className="truncate opacity-80">{message.reply_to.content || message.reply_to.message_preview}</p>
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`rounded-2xl px-[10px] py-[6px] ${isOwn
            ? isDark
              ? 'bg-blue-700 text-white rounded-tr-sm'
              : 'bg-[#2563eb] text-white rounded-tr-sm'
            : isDark
              ? 'bg-gray-700 text-gray-100 rounded-tl-sm'
              : 'bg-[#E9EDEF] text-gray-900 rounded-tl-sm'
            } ${isPending ? 'opacity-70' : ''} ${isFailed ? 'border-2 border-red-500' : ''}`}
        >
          {/* Sender name for non-direct threads - clickable with role badge */}
          {!isOwn && threadMemberCount > 1 && (
            <button
              onClick={() => onAvatarClick?.(message)}
              className={`flex items-center gap-1 text-xs font-semibold mb-1 hover:underline cursor-pointer ${isDark ? 'text-blue-400' : 'text-blue-600'
                }`}
            >
              <span>{message.sender_name}</span>
              {/* Role badge inline with name */}
              {senderRole && ROLE_BADGES[senderRole.toLowerCase()] && (
                <span className={`px-1 py-0.5 rounded text-[9px] text-white ${ROLE_BADGES[senderRole.toLowerCase()].color}`}>
                  {ROLE_BADGES[senderRole.toLowerCase()].label}
                </span>
              )}
            </button>
          )}

          {/* Location message */}
          {isLocationMessage && message.location && locationMapHref && (
            <a
              href={locationMapHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`mb-2 block overflow-hidden rounded-xl border transition-opacity hover:opacity-90 ${isOwn
                ? 'border-white/20 bg-white/10'
                : isDark
                  ? 'border-gray-600 bg-gray-800/80'
                  : 'border-gray-300 bg-white'
                }`}
              style={{ marginTop: '-4px' }}
            >
              <div className={`relative aspect-[16/9] w-full overflow-hidden ${isOwn ? 'bg-white/5' : isDark ? 'bg-gray-900' : 'bg-gray-100'
                }`}>
                {locationPreviewUrl && !locationPreviewFailed ? (
                  <img
                    src={locationPreviewUrl}
                    alt={message.location.address || 'Shared location preview'}
                    className="h-full w-full object-cover"
                    onError={() => setLocationPreviewFailed(true)}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <MapPin className="h-10 w-10 text-red-500" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                <div className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/95 px-2 py-1 text-[11px] font-semibold text-gray-900 shadow-sm">
                  <MapPin className="h-3.5 w-3.5 text-red-500" />
                  Shared location
                </div>
                <div className="absolute bottom-3 right-3 rounded-full bg-black/75 px-2 py-1 text-[11px] font-medium text-white">
                  Open in Maps
                </div>
              </div>
              <div className="flex items-start gap-3 px-3 py-3">
                <div className="mt-0.5 rounded-full bg-red-500/15 p-2">
                  <MapPin className="h-4 w-4 text-red-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">Shared location</p>
                  <p className={`mt-0.5 text-xs leading-5 ${isOwn ? 'text-white/80' : isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {message.location.address || `${message.location.latitude.toFixed(6)}, ${message.location.longitude.toFixed(6)}`}
                  </p>
                  <p className={`mt-1 text-[11px] ${isOwn ? 'text-white/60' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {message.location.latitude.toFixed(5)}, {message.location.longitude.toFixed(5)}
                    {typeof message.location.accuracy === 'number' && Number.isFinite(message.location.accuracy)
                      ? ` · ±${Math.round(message.location.accuracy)}m`
                      : ''}
                  </p>
                </div>
                <ExternalLink className={`mt-1 h-4 w-4 flex-shrink-0 ${isOwn ? 'text-white/70' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              </div>
            </a>
          )}

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="space-y-2 mb-2" style={{ marginTop: '-4px' }}>
              {message.attachments.map((att, attIndex) => (
                <div key={att.id || attIndex}>
                  {/* Voice note */}
                  {isVoiceNote && att.contentType?.startsWith('audio/') ? (
                    <div className="flex items-center gap-2">
                      <Mic className={`w-5 h-5 ${isOwn ? 'text-white/80' : 'text-gray-500'}`} />
                      <audio
                        src={att.url}
                        controls
                        className="flex-1 h-8"
                        style={{ maxWidth: '200px' }}
                      />
                    </div>
                  ) : att.isImage ? (
                    <div
                      className="relative group/img cursor-pointer"
                      onClick={() => onImageClick?.(att.url)}
                    >
                      <img
                        src={att.url}
                        alt={att.originalFilename}
                        className="max-w-full rounded-lg"
                        style={{ maxHeight: '200px' }}
                      />
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/img:opacity-100 transition-opacity">
                        <a
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-full bg-black/50 hover:bg-black/70"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Eye className="w-4 h-4 text-white" />
                        </a>
                        <a
                          href={att.url}
                          download={att.originalFilename}
                          className="p-1.5 rounded-full bg-black/50 hover:bg-black/70"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download className="w-4 h-4 text-white" />
                        </a>
                      </div>
                    </div>
                  ) : att.isVideo ? (
                    <div className="relative">
                      <video
                        src={att.url}
                        controls
                        className="max-w-full rounded-lg"
                        style={{ maxHeight: '200px' }}
                      />
                    </div>
                  ) : (
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 p-2 rounded-lg ${isOwn ? 'bg-white/10' : isDark ? 'bg-gray-600' : 'bg-white'
                        } hover:opacity-80 transition-opacity border ${isOwn ? 'border-white/20' : isDark ? 'border-gray-500' : 'border-gray-200'}`}
                    >
                      <FileText className={`w-5 h-5 flex-shrink-0 ${isOwn ? 'text-white/80' : isDark ? 'text-gray-300' : 'text-gray-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isOwn ? 'text-white' : isDark ? 'text-gray-200' : 'text-gray-900'}`}>{att.originalFilename}</p>
                        <p className={`text-xs ${isOwn ? 'text-white/60' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatFileSize(att.size)}
                        </p>
                      </div>
                      <Download className={`w-4 h-4 flex-shrink-0 ${isOwn ? 'text-white/70' : isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAiOpsReviewSummaryCard && (
            <div
              className={`mb-2 rounded-xl border px-2.5 py-2 text-left ${isOwn
                ? 'border-white/20 bg-white/10'
                : isDark
                  ? 'border-gray-500/50 bg-gray-600/50'
                  : 'border-gray-200 bg-white'
                }`}
              style={{ marginTop: '-4px' }}
            >
              <div className="flex flex-wrap items-center gap-2">
                {aiVerdictRaw && (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${aiVerdictRaw === 'block'
                      ? isOwn
                        ? 'bg-red-500/35 text-white'
                        : isDark
                          ? 'bg-red-900/50 text-red-200'
                          : 'bg-red-100 text-red-800'
                      : aiVerdictRaw === 'watch'
                        ? isOwn
                          ? 'bg-amber-500/35 text-white'
                          : isDark
                            ? 'bg-amber-900/50 text-amber-200'
                            : 'bg-amber-100 text-amber-800'
                        : isOwn
                          ? 'bg-white/15 text-white'
                          : isDark
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                  >
                    {aiVerdictRaw === 'block'
                      ? 'Verdict: block'
                      : aiVerdictRaw === 'watch'
                        ? 'Verdict: watch'
                        : `Verdict: ${aiVerdictRaw}`}
                  </span>
                )}
                {confidenceFromMeta != null && confidenceFromMeta <= 100 && (
                  <span
                    className={`text-[10px] font-medium ${isOwn ? 'text-white/70' : isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}
                  >
                    Confidence {confidenceFromMeta}%
                  </span>
                )}
              </div>
              {showAiOpsFieldPhotoLine && fieldPhotoCountMeta != null && (
                <div
                  className={`mt-2 flex items-start gap-2 text-[11px] leading-snug ${isOwn ? 'text-white/80' : isDark ? 'text-gray-200' : 'text-gray-700'
                    }`}
                >
                  <Camera className={`mt-0.5 h-3.5 w-3.5 shrink-0 opacity-90 ${isOwn ? 'text-white/70' : isDark ? 'text-gray-400' : 'text-gray-500'}`} aria-hidden />
                  <span>
                    <span className="font-medium">Field photos</span>
                    {': '}
                    {fieldPhotoCountMeta} saved
                    {fieldPhotoFlaggedMeta != null && fieldPhotoFlaggedMeta > 0 ? (
                      <span
                        className={
                          isOwn ? 'text-amber-200' : isDark ? 'text-amber-300' : 'text-amber-700'
                        }
                      >
                        {` · ${fieldPhotoFlaggedMeta} flagged by AI`}
                      </span>
                    ) : (
                      <span className="opacity-80"> · none flagged</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Message content (hide if it's just attachment placeholder or voice note) */}
          {message.content && message.content !== '📎 Attachment' && message.content !== '🎤 Voice note' && !isLocationMessage && (
            <div style={{ marginTop: '-4px' }}>
              <div
                className={`relative ${shouldCollapseAiOpsReviewBody && !aiOpsBodyExpandedEffective
                  ? 'max-h-[9.5rem] overflow-hidden'
                  : ''
                  }`}
              >
                {shouldCollapseAiOpsReviewBody && !aiOpsBodyExpandedEffective && (
                  <div
                    className={`pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t ${isOwn
                      ? isDark
                        ? 'from-blue-700'
                        : 'from-[#2563eb]'
                      : isDark
                        ? 'from-gray-700'
                        : 'from-[#E9EDEF]'
                      } to-transparent`}
                    aria-hidden
                  />
                )}
                <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed">
                  {typeof message.content === 'object'
                    && message.content !== null
                    && 'encrypted' in message.content
                    && Boolean((message.content as Record<string, unknown>).encrypted)
                    ? <span className="italic text-gray-400">🔐 Encrypted message</span>
                    : searchQuery && isSearchMatch
                      ? highlightSearchMatch(String(message.content), searchQuery)
                      : renderContentWithLinks(String(message.content), isDark, isOwn)
                  }
                </p>
              </div>
              {shouldCollapseAiOpsReviewBody && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAiOpsDetailsExpanded((open) => !open);
                  }}
                  className={`mt-1.5 text-xs font-semibold underline-offset-2 hover:underline ${isOwn ? 'text-white/80' : isDark ? 'text-blue-400' : 'text-blue-600'
                    }`}
                >
                  {aiOpsBodyExpandedEffective ? 'Hide details' : 'Show details'}
                </button>
              )}
            </div>
          )}

          {showAiReferenceFooter && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {isAiOperationsReviewAlert && (
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold ${reviewStatus === 'resolved'
                  ? isOwn ? 'bg-white/20 text-white' : isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                  : reviewStatus === 'acknowledged'
                    ? isOwn ? 'bg-white/20 text-white' : isDark ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'
                    : isOwn ? 'bg-white/20 text-white' : isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'
                  }`}>
                  {reviewStatusLabel}
                </span>
              )}

              {canShowAppointmentReference && (
                appointmentAiReviewHref ? (
                  <a
                    href={appointmentAiReviewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={referencePillClass}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {appointmentReferenceLabel}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => openWorkItemWithAiReview(
                      message.metadata?.appointment_id,
                      message.metadata?.appointment_permalink_url
                    )}
                    className={referencePillClass}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {appointmentReferenceLabel}
                  </button>
                )
              )}

              {canShowJobReference && (
                jobAiReviewHref ? (
                  <a
                    href={jobAiReviewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={referencePillClass}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {jobReferenceLabel}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => openWorkItemWithAiReview(
                      message.metadata?.parent_job_id || message.metadata?.job_id,
                      message.metadata?.parent_job_permalink_url || message.metadata?.job_permalink_url
                    )}
                    className={referencePillClass}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {jobReferenceLabel}
                  </button>
                )
              )}

              {canShowReviewRecordReference && (
                reviewRecordAiReviewHref ? (
                  <a
                    href={reviewRecordAiReviewHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={referencePillClass}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Review record
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => openWorkItemWithAiReview(
                      message.metadata?.review_item_id,
                      message.metadata?.review_item_permalink_url
                    )}
                    className={referencePillClass}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Review record
                  </button>
                )
              )}

              {canShowProposalReference && (
                proposalPermalinkHref ? (
                  <a
                    href={proposalPermalinkHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={referencePillClass}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {proposalReferenceLabel}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => openPermalinkDirect(message.metadata?.proposal_url)}
                    className={referencePillClass}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {proposalReferenceLabel}
                  </button>
                )
              )}

              {canShowProposalPdf && (
                <a
                  href={proposalPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={referencePillClass}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="w-3 h-3" />
                  Proposal PDF
                </a>
              )}

              {canEmailProposal && (
                <button
                  type="button"
                  onClick={() => proposalId && onOpenProposalEmail?.(proposalId)}
                  disabled={isProposalEmailLoading}
                  className={referencePillClass}
                >
                  <Mail className="w-3 h-3" />
                  {isProposalEmailLoading ? 'Loading email…' : 'Email proposal'}
                </button>
              )}

              {canAcknowledgeReview && onReviewAction && (
                <button
                  onClick={() => handleReviewActionClick('acknowledge')}
                  disabled={Boolean(reviewActionLoading)}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-60 ${isOwn ? 'bg-white/15 text-white' : isDark ? 'bg-sky-900/40 text-sky-300' : 'bg-sky-100 text-sky-700'
                    }`}
                >
                  {reviewActionLoading === 'acknowledge' ? 'Saving…' : 'Acknowledge'}
                </button>
              )}

              {canAiHoldReview && onReviewAction && (
                <button
                  onClick={() => handleReviewActionClick('ai_hold')}
                  disabled={Boolean(reviewActionLoading)}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-60 ${isOwn ? 'bg-white/15 text-white' : isDark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700'
                    }`}
                >
                  {reviewActionLoading === 'ai_hold' ? 'Saving…' : 'AI Hold'}
                </button>
              )}

              {canResumeReview && onReviewAction && (
                <button
                  onClick={() => handleReviewActionClick('resume_review')}
                  disabled={Boolean(reviewActionLoading)}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-60 ${isOwn ? 'bg-white/15 text-white' : isDark ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700'
                    }`}
                >
                  {reviewActionLoading === 'resume_review' ? 'Saving…' : 'Resume review'}
                </button>
              )}

              {canResolveReview && onReviewAction && (
                <button
                  onClick={() => handleReviewActionClick('resolve')}
                  disabled={Boolean(reviewActionLoading)}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-opacity hover:opacity-80 disabled:opacity-60 ${isOwn ? 'bg-white/15 text-white' : isDark ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                    }`}
                >
                  {reviewActionLoading === 'resolve' ? 'Saving…' : 'Accept review'}
                </button>
              )}
            </div>
          )}

          {/* Pending/Failed indicator */}
          {isPending && !isFailed && (
            <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>Sending...</p>
          )}
          {isFailed && (
            <p className="text-xs mt-1 text-red-400">Failed to send - tap to retry</p>
          )}

          {/* Timestamp and read receipts - inside bubble */}
          <div className={`flex items-center justify-end gap-1 mt-1.5 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
            <span className="text-[11px]">
              {formatTimeSAST(message.timestamp)}
            </span>
            {isOwn && (
              <span className={`flex items-center ${allRead ? 'text-white/90' : someRead ? 'text-white/70' : 'text-white/50'}`} title={
                readByOthers.length > 0
                  ? `Read by: ${readByOthers.map(r => r.user_name || 'Member').join(', ')}`
                  : 'Sent'
              }>
                <CheckCheck className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
        </div>

        {/* Reactions display */}
        {message.reactions && message.reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(
              message.reactions.reduce((acc, r) => {
                if (!acc[r.emoji]) {
                  acc[r.emoji] = { emoji: r.emoji, users: [], count: 0 };
                }
                acc[r.emoji].users.push(r.user_name);
                acc[r.emoji].count++;
                return acc;
              }, {} as Record<string, { emoji: string; users: string[]; count: number }>)
            ).map(([emoji, data]) => (
              <button
                key={emoji}
                onClick={() => messageId && onReaction?.(messageId, emoji)}
                className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs transition-colors ${message.reactions?.some(r => r.user_id === currentUserId && r.emoji === emoji)
                  ? isDark
                    ? 'bg-blue-600/30 border border-blue-500'
                    : 'bg-blue-100 border border-blue-400'
                  : isDark
                    ? 'bg-gray-700 hover:bg-gray-600'
                    : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                title={data.users.join(', ')}
              >
                <span className="text-sm">{data.emoji}</span>
                {data.count > 1 && (
                  <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {data.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}


        {/* Action buttons (hover) */}
        <div className={`absolute ${isOwn ? 'left-0 -translate-x-full pr-1' : 'right-0 translate-x-full pl-1'} top-1 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5`}>
          {/* Reply */}
          <button
            onClick={() => onReply?.(message)}
            className={`p-1.5 rounded-full ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
            title="Reply"
          >
            <Reply className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>

          {/* Quick reaction */}
          <div className="relative">
            <button
              onClick={() => setShowReactionPicker(!showReactionPicker)}
              className={`p-1.5 rounded-full ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              title="React"
            >
              <span className="text-sm">😊</span>
            </button>

            {showReactionPicker && (
              <div className={`absolute ${isOwn ? 'right-0' : 'left-0'} bottom-full mb-1 flex gap-1 p-1 rounded-full shadow-lg ${isDark ? 'bg-gray-800' : 'bg-white'
                }`}>
                {QUICK_REACTIONS.map(emoji => (
                  <button
                    key={emoji}
                    onClick={() => {
                      if (messageId) {
                        onReaction?.(messageId, emoji);
                      }
                      setShowReactionPicker(false);
                    }}
                    className={`text-lg hover:scale-125 transition-transform p-1`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* More actions */}
          <div className="relative" ref={actionMenuRef}>
            <button
              onClick={() => {
                if (!showActionMenu && actionMenuRef.current) {
                  const rect = actionMenuRef.current.getBoundingClientRect();
                  setActionMenuPos({ top: rect.bottom + 4, left: isOwn ? rect.right - 176 : rect.left });
                }
                setShowActionMenu(!showActionMenu);
              }}
              className={`p-1.5 rounded-full ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              title="More"
            >
              <MoreVertical className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            </button>

            {showActionMenu && actionMenuPos && createPortal(
              <div className={`fixed w-44 rounded-lg shadow-lg border z-[9999] ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`} style={{ top: actionMenuPos.top, left: actionMenuPos.left }}>
                {/* Add to managed tasks */}
                {onAddToTasks && (
                  <button
                    onClick={() => {
                      onAddToTasks(message);
                      setShowActionMenu(false);
                    }}
                    disabled={isAddingToTask || isInTasks}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors first:rounded-t-lg ${isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                      } ${isInTasks ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <CheckSquare className="w-4 h-4" />
                    {isInTasks ? 'Already in managed tasks' : isAddingToTask ? 'Adding...' : 'Add to Managed Tasks'}
                  </button>
                )}

                {/* Pin mention (if mentions current user) */}
                {message.mentions?.includes(currentUserId || 0) && onPinMention && (
                  <button
                    onClick={() => {
                      onPinMention(message);
                      setShowActionMenu(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                  >
                    <Pin className="w-4 h-4" />
                    Pin Mention
                  </button>
                )}

                {/* Delete message — own messages only */}
                {onDelete && isOwn && (
                  <button
                    onClick={() => {
                      onDelete(messageId);
                      setShowActionMenu(false);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors border-t last:rounded-b-lg ${isDark ? 'hover:bg-gray-700 text-red-400 border-gray-700' : 'hover:bg-gray-100 text-red-600 border-gray-200'
                      }`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
